import {
  elementsTransition,
  getSelectedGamePackIcon,
  getSelectedGamePackPalette
} from '@/utils/gamePacks';
import clsx from 'clsx';
import TileCrownShape from './BackgroundElements/TileCrownShape';
import TileDynamicShape from './BackgroundElements/TileDynamicShape';
import TileFixedShape from './BackgroundElements/TileFixedShape';
import TileFixedWhiteLeftShape from './BackgroundElements/TileFixedWhiteLeftShape';
import TileFixedWhiteRightShape from './BackgroundElements/TileFixedWhiteRightShape';
import TileInverseDynamicLeftShape from './BackgroundElements/TileInverseDynamicLeftShape';
import TileInverseDynamicRightShape from './BackgroundElements/TileInverseDynamicRightShape';
import { motion } from 'motion/react';
import { GamePackIcon, GamePackPalette } from '@/API';

export default function TileBackground({
  gamepackPalette,
  gamepackIcon,
  hover,
  isNewGamePack
}: {
  gamepackPalette: GamePackPalette;
  gamepackIcon: GamePackIcon;
  hover: boolean;
  isNewGamePack: boolean;
}) {
  const palette = getSelectedGamePackPalette(gamepackPalette);
  const icon = getSelectedGamePackIcon(gamepackIcon);

  if (!palette || !icon) return <></>;

  return (
    <div
      className={clsx(
        'z-5 overflow-hidden rounded-2xs w-full h-184 shrink-0 relative group-hover:absolute group-hover:h-full group-hover:w-full group-hover:left-0 group-hover:top-0 z-0',
        palette.colors.bg
      )}
    >
      <div
        className={clsx('w-full h-full absolute', `${hover ? palette.colors.hover : 'hidden'}`)}
      ></div>
      <TileFixedShape
        className={clsx('absolute top-0 right-0 origin-top-right')}
        variants={{
          hover: { scale: 1 },
          default: { scale: 0.75 }
        }}
        animate={hover ? 'hover' : 'default'}
        transition={elementsTransition}
      />
      <TileFixedWhiteLeftShape
        className={clsx('absolute top-0 left-0 origin-top-left')}
        variants={{
          hover: { scale: 1 },
          default: { scale: 0.75 }
        }}
        animate={hover ? 'hover' : 'default'}
        transition={elementsTransition}
      />
      <TileInverseDynamicLeftShape
        className={clsx('absolute bottom-0 left-0 origin-bottom-left')}
        variants={{
          hover: { scale: 1 },
          default: { scale: 0.75 }
        }}
        animate={hover ? 'hover' : 'default'}
        color={palette.colors.inverse}
        transition={elementsTransition}
      />
      <TileInverseDynamicRightShape
        className={clsx('absolute bottom-0 right-0 origin-bottom-right')}
        variants={{
          hover: { scale: 1 },
          default: { scale: 0.75 }
        }}
        animate={hover ? 'hover' : 'default'}
        color={palette.colors.inverse}
        transition={elementsTransition}
      />
      <motion.img
        src={icon.icons.white}
        alt={icon.label}
        className={clsx(
          'absolute select-none',
          hover ? 'w-88 h-88 right-8 top-8' : 'w-130 h-130 left-11 top-27'
        )}
        layout
        transition={elementsTransition}
      />
      <TileFixedWhiteRightShape
        className={clsx('absolute right-0 top-47 origin-top-right')}
        variants={{
          hover: { scale: 1 },
          default: { scale: 0.75 }
        }}
        animate={hover ? 'hover' : 'default'}
      />
      <TileCrownShape
        className={clsx('absolute bottom-18 origin-bottom-left scale-75')}
        variants={{
          hover: { x: -50 },
          default: { x: 11 }
        }}
        animate={hover ? 'hover' : 'default'}
        transition={elementsTransition}
      />
      <TileDynamicShape
        className={clsx('absolute bottom-0 right-0 origin-bottom-right')}
        color={palette.colors.shapes}
        variants={{
          hover: { scale: 1 },
          default: { scale: 0.75 }
        }}
        animate={hover ? 'hover' : 'default'}
      />
      {isNewGamePack && (
        <motion.p
          className={clsx(
            'uppercase text-[10px] leading-[10px] block px-10 py-5 border-[0.5px] border-white bg-accent-1-500 rounded-xl absolute  text-secondary-navy-500 font-semibold',
            hover ? 'top-16 left-16' : 'top-8 left-8'
          )}
          layout
          transition={elementsTransition}
        >
          New Pack
        </motion.p>
      )}
    </div>
  );
}
